import { Stack } from '@mui/material';
import { AssessmentStatuses } from '@schooly/constants';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import {
  AssessmentStatusTagSelect,
  AssessmentStatusTagSelectProps,
} from './AssessmentStatusTagSelect';

type AssessmentStatusExpandedSelectProps = PropsWithChildren<{
  selectedStatuses: AssessmentStatuses[];
  onApply: (v: AssessmentStatuses[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

const OPTIONS = [AssessmentStatuses.Published, AssessmentStatuses['Not published']];

export const AssessmentStatusExpandedSelect: FC<AssessmentStatusExpandedSelectProps> = ({
  selectedStatuses: initialSelectedStatuses,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedStatuses, setSelectedStatuses] =
    useState<AssessmentStatuses[]>(initialSelectedStatuses);

  const toggleStatuses = useCallback((v: AssessmentStatuses[]) => {
    setSelectedStatuses((statuses) => toggleMultipleValueArrayProperty(statuses, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedStatuses);
    onClose();
  }, [selectedStatuses, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderAssessmentStatusTags({
            statuses: OPTIONS,
            tagProps: (status) => ({
              variant: selectedStatuses.includes(status) ? 'filled' : undefined,
            }),
            onClick: (status) => toggleStatuses([status]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [toggleStatuses, selectedStatuses, handleApply, onClear]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedStatuses.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
    >
      {renderAssessmentStatusTags({
        tagProps: { size: 'small' },
        statuses: selectedStatuses,
        onDelete: (status) => toggleStatuses([status]),
      })}
    </ExpandedSelect>
  );
};

type RenderAssessmentStatusTagsParams = {
  statuses: AssessmentStatuses[];
  onDelete?: (v: AssessmentStatuses) => void;
  onClick?: (v: AssessmentStatuses) => void;
  tagProps?:
    | Omit<AssessmentStatusTagSelectProps, 'status'>
    | ((v: AssessmentStatuses) => Omit<AssessmentStatusTagSelectProps, 'status'>);
};

export const renderAssessmentStatusTags = ({
  statuses,
  onDelete,
  onClick,
  tagProps,
}: RenderAssessmentStatusTagsParams) =>
  statuses.map((v) => (
    <AssessmentStatusTagSelect
      key={v}
      status={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
